import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Paginations from "../Customs/pagination/Paginations";
import WebService from "../Services/WebService";
import styles from "./RawMaterialDetailPage.module.css";
import { InventoryItemType, StockHistoryView } from "./inventoryHistory";

interface HistoryItem {
  id?: number;
  sku_id?: string;
  raw_material_id?: number;
  product_id?: number;
  order_type?: string;
  order_type_label?: string;
  transaction_type?: string;
  transaction_type_label?: string;
  direction?: string;
  quantity?: number;
  quantity_in?: number;
  quantity_out?: number;
  running_quantity?: number;
  order_id?: number | string;
  order_number?: string;
  reference_type?: string;
  party_type?: string;
  party_name?: string;
  unit_price?: number | string;
  line_amount?: number | string;
  order_status?: string;
  order_status_label?: string;
  summary?: string;
  reason?: number;
  reason_label?: string;
  additional_note?: string;
  created_by_name?: string;
  created_at?: string;
}

interface PaginationInfo {
  limit?: number;
  offset?: number;
  total_records?: number;
  has_next?: boolean;
}

const REASON_MAP: Record<number, string> = {
  0: "-",
  1: "Stock adjustment",
  2: "Damage / loss",
};

function formatDate(value?: string) {
  if (!value) return "--";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-IN");
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "--" : d.toLocaleDateString("en-IN");
}

function formatQty(value?: number | string | null) {
  if (value === null || value === undefined || value === "") return "0";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function formatMoney(value?: number | string | null) {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "—";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface StockHistoryProps {
  itemType: InventoryItemType;
  itemId: number;
  view: StockHistoryView;
}

const StockHistory: React.FC<StockHistoryProps> = ({ itemType, itemId, view }) => {
  const [rows, setRows] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const [orderId, setOrderId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const isInventory = view === StockHistoryView.INVENTORY;
  const isProduct = itemType === InventoryItemType.PRODUCT;

  const historyPath = isProduct
    ? `/v1/door-management/product/history/${itemId}`
    : `/v1/door-management/raw-material/history/${itemId}`;

  const validateDates = (from: string, to: string) => !(from && to && new Date(from) > new Date(to));

  const fetchHistory = async (
    nextPage: number,
    ordIdVal = orderId,
    fromDVal = fromDate,
    toDVal = toDate,
    typeVal = transactionType
  ) => {
    if (!itemId) return;
    if (!validateDates(fromDVal, toDVal)) return;
    setLoading(true);

    const query = new URLSearchParams();
    query.append("page_number", String(nextPage));
    query.append("limit", String(pageSize));
    query.append("view", view);
    if (ordIdVal.trim()) query.append("order_id", ordIdVal.trim());
    if (fromDVal) query.append("from_date", fromDVal);
    if (toDVal) query.append("to_date", toDVal);
    if (typeVal) query.append("type", typeVal);

    try {
      const res = await WebService.getAPI<any>({
        action: `${historyPath}?${query.toString()}`,
      });
      const history: HistoryItem[] = res?.data?.history ?? res?.history ?? [];
      const pagination: PaginationInfo = res?.data?.pagination ?? res?.pagination ?? {};
      setRows(history || []);
      setTotal(pagination.total_records ?? history.length ?? 0);
      setPage(nextPage);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOrderId("");
    setFromDate("");
    setToDate("");
    setTransactionType("");
    fetchHistory(1, "", "", "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, itemType, view]);

  const handleOrderIdChange = (val: string) => {
    const numericVal = val.replace(/\D/g, "");
    if (numericVal === orderId) return;
    setOrderId(numericVal);
    setPage(1);
    fetchHistory(1, numericVal, fromDate, toDate, transactionType);
  };

  const handleFromDateChange = (val: string) => {
    setFromDate(val);
    setPage(1);
    if (validateDates(val, toDate)) fetchHistory(1, orderId, val, toDate, transactionType);
  };

  const handleToDateChange = (val: string) => {
    setToDate(val);
    setPage(1);
    if (validateDates(fromDate, val)) fetchHistory(1, orderId, fromDate, val, transactionType);
  };

  const handleTransactionTypeChange = (val: string) => {
    setTransactionType(val);
    setPage(1);
    fetchHistory(1, orderId, fromDate, toDate, val);
  };

  const handleClearFilters = () => {
    setOrderId("");
    setFromDate("");
    setToDate("");
    setTransactionType("");
    setPage(1);
    fetchHistory(1, "", "", "", "");
  };

  const handlePageChange = (nextPage: number) =>
    fetchHistory(nextPage, orderId, fromDate, toDate, transactionType);

  const renderOrderType = (row: HistoryItem) => {
    const normalized = (row.transaction_type || row.order_type || "").toUpperCase();
    let badgeClass = styles.orderPurchase;
    if (normalized.includes("SALE") && !normalized.includes("USED")) badgeClass = styles.orderSale;
    if (normalized.includes("USED")) badgeClass = styles.orderUsed;
    if (normalized.includes("ADJUST")) badgeClass = styles.orderAdjustment;
    return (
      <span className={`${styles.orderTypeBadge} ${badgeClass}`}>
        {row.transaction_type_label || row.order_type_label || normalized || "N/A"}
      </span>
    );
  };

  const orderHref = (row: HistoryItem) => {
    if (row.order_id == null || row.order_id === "") return null;
    const ref = (row.reference_type || "").toUpperCase();
    const type = (row.transaction_type || row.order_type || "").toUpperCase();
    if (ref === "PURCHASE_ORDER" || type === "PURCHASE") {
      return `/purchase-order/${row.order_id}`;
    }
    return `/saleorder/${row.order_id}`;
  };

  const renderReason = (row: HistoryItem) => {
    if (row.reason_label) return row.reason_label;
    if (row.reason === undefined || row.reason === null) return "N/A";
    return REASON_MAP[row.reason] || `Reason ${row.reason}`;
  };

  const showingStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingEnd = total === 0 ? 0 : Math.min(total, (page - 1) * pageSize + rows.length);

  return (
    <div className={styles.historyCard}>
      <div className={styles.historyHeader}>
        <div>
          <div className={styles.cardTitle}>{isInventory ? "Inventory History" : "Transaction History"}</div>
          <div className={styles.historySubtitle}>
            {isInventory
              ? "Stock in, stock out, and running balance from posted inventory movements"
              : "Sales, purchases, and related orders for this item"}
          </div>
        </div>
      </div>

      <div className={styles.historyFilterRow}>
        <input
          type="text"
          inputMode="numeric"
          value={orderId}
          onChange={(e) => handleOrderIdChange(e.target.value.replace(/\D/g, ""))}
          className={styles.historyFilterInput}
          placeholder={isInventory ? "Search ID…" : "Search order no…"}
        />
        <select
          className={styles.historyFilterInput}
          value={transactionType}
          onChange={(e) => handleTransactionTypeChange(e.target.value)}
        >
          <option value="">All types</option>
          {isInventory ? (
            <>
              <option value="STOCK_ADJUSTMENT">Stock adjustment</option>
              <option value="NEW">New stock</option>
              <option value="PURCHASE_ORDER_RECEIVE">Purchase receive</option>
              <option value="PURCHASE_ORDER_RECEIVE_REVERSAL">Purchase reversal</option>
              <option value="SALE_ORDER_ACQUISITION">Sale issue</option>
              <option value="SALE">Sale</option>
              <option value="PURCHASE">Purchase</option>
              <option value="ADJUSTMENT">Adjustment</option>
            </>
          ) : (
            <>
              <option value="SALE">Sold</option>
              <option value="PURCHASE">Purchased</option>
              <option value="USED_IN_SALE">Used in sale</option>
            </>
          )}
        </select>
        <input
          type="date"
          className={styles.historyFilterInput}
          value={fromDate}
          onChange={(e) => handleFromDateChange(e.target.value)}
          max={toDate || undefined}
        />
        <input
          type="date"
          className={styles.historyFilterInput}
          value={toDate}
          onChange={(e) => handleToDateChange(e.target.value)}
          min={fromDate || undefined}
        />
        {(orderId || fromDate || toDate || transactionType) && (
          <button className={styles.historyFilterButtonGhost} onClick={handleClearFilters} disabled={loading}>
            Clear All
          </button>
        )}
      </div>

      <div className={styles.historyTableWrapper}>
        {loading ? (
          <div className={styles.historyEmpty}>Loading history...</div>
        ) : rows.length === 0 ? (
          <div className={styles.historyEmpty}>
            {isInventory ? "No inventory movements found for this item." : "No sales or purchases found for this item."}
          </div>
        ) : (
          <table className={styles.historyTable}>
            <thead>
              <tr>
                {isInventory ? (
                  <>
                    <th>Date</th>
                    <th>Movement</th>
                    <th>In</th>
                    <th>Out</th>
                    <th>Balance</th>
                    <th>Reason</th>
                    <th>Added By</th>
                  </>
                ) : (
                  <>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Order</th>
                    <th>Party</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`${row.id ?? row.order_id}-${idx}`}>
                  {isInventory ? (
                    <>
                      <td>{formatDate(row.created_at)}</td>
                      <td>{renderOrderType(row)}</td>
                      <td><span className={styles.quantityPos}>{formatQty(row.quantity_in)}</span></td>
                      <td><span className={styles.quantityNeg}>{formatQty(row.quantity_out)}</span></td>
                      <td>{formatQty(row.running_quantity)}</td>
                      <td>{renderReason(row)}</td>
                      <td>{row.created_by_name && row.created_by_name !== "-" ? row.created_by_name : "-"}</td>
                    </>
                  ) : (
                    <>
                      <td>{formatDate(row.created_at)}</td>
                      <td>{renderOrderType(row)}</td>
                      <td>
                        {(() => {
                          const href = orderHref(row);
                          const label = row.order_number || (row.order_id != null ? String(row.order_id) : "—");
                          return href ? (
                            <Link className={styles.historyOrderLink} to={href}>
                              {label}
                            </Link>
                          ) : (
                            label
                          );
                        })()}
                      </td>
                      <td>
                        <div className={styles.historyParty}>
                          <span className={styles.historyDirection}>{row.direction || "—"}</span>
                          <span>{row.party_name || "—"}</span>
                        </div>
                      </td>
                      <td>{formatQty(row.quantity)}</td>
                      <td>{formatMoney(row.unit_price)}</td>
                      <td>{formatMoney(row.line_amount)}</td>
                      <td>{row.order_status_label || row.order_status || "—"}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.paginationRow}>
        <div className={styles.paginationMeta}>
          {total === 0 ? "No records" : `Showing ${showingStart}-${showingEnd} of ${total}`}
        </div>
        {total > 0 && (
          <Paginations
            totalItems={total}
            itemsCountPerPage={pageSize}
            changePage={handlePageChange}
            changeCurrentPage={page}
          />
        )}
      </div>
    </div>
  );
};

export default StockHistory;
